import React from 'react';
import { connect } from 'react-redux';
import { t } from '@lingui/macro';
import * as T from '../../constants/types';
import { withDisplayedLayersCheck } from '../../HOC/withDisplayedLayersCheck';
import { SCR_TRACKS_STYLE } from '../../constants/layerStyles';
import { SCR_TRACKS_WIDTH_BASE } from '../../constants/layers';
import { fetchShiftTileFeatures } from '../../utils/scrTrackTiles';
import { setScrKeyPoints, clearScrKeyPoints } from '../../utils/scrKeyPointRegistry';
import { onChangeMapFiltersMulti } from '../../ac/onChangeMapFilters';
import { MapTimeline } from './MapTimeline';

// Цвет роли исполнителя — чтобы точки-отсечки совпадали по цвету с треком.
const roleColor = role => {
    const found = SCR_TRACKS_STYLE.roles.find(r => r.value === role);
    return found ? found.color : '#757575';
};
/**
 * Привязка MapTimeline к слою «Треки исполнителей» (DA-14933).
 *
 * Два режима:
 *   1. СУТКИ (по умолчанию) — continuous-домен часов 0–24 + контрол даты, выбор
 *      ДИАПАЗОНА двумя бегунками (DA-15115). Границы диспатчатся в
 *      mapFilters.scrTracksHourFrom/To; треки, пересекающие окно, остаются в своём
 *      цвете, прочие бледнеют — это делает Map.applyScrTracksStyle. По умолчанию
 *      [0,24] — все сутки, ничего не приглушается.
 *   2. СМЕНА — включается при выборе смены (кнопка «Детали смены» в попапе →
 *      map.scrTracksSelectedShiftId). Continuous-домен [старт−1ч, конец+1ч] смены
 *      (DA-15115: контекст до/после, фиксированное число делений SHIFT_TICKS_COUNT,
 *      явная засечка на полночь с датой, если смена пересекает сутки), риски-отсечки
 *      между сегментами, Play/Pause гоняет точку исполнителя по сегментам.
 *      Данные догружаются за 2 даты (selectedScrTracksDate и следующую) — иначе смена,
 *      переваливающая через полночь, обрезалась бы на границе суток.
 *
 * Геометрия/времена сегментов берутся из загруженных тайлов (querySourceFeatures/прямая
 * догрузка z12-тайлов), точка исполнителя рендерится в свой GeoJSON-источник scr-runner.
 *
 * Линия выбранной смены (scr-selected-line, DA-15115) — тоже клиентский GeoJSON, а НЕ
 * тайловый слой «Треки исполнителей»: у тайлового слоя линия зависит от зума (z<12 —
 * упрощённая обзорная ветка, ST_LineMerge+ST_SimplifyPreserveTopology — геометрическое
 * слияние без учёта хронологии) и от одной даты (map-источник знает только
 * selectedScrTracksDate, вторые сутки смены на нём не отображались бы). Бегунок и риски
 * уже строятся из точного, хронологически верного и дособранного за 2 даты массива
 * segments — линия теперь рисуется из ТЕХ ЖЕ данных, чтобы гарантированно совпадать
 * с траекторией воспроизведения на любом зуме. Тайловая линия при этом гасится для
 * выбранной смены (Map.jsx: applyScrTracksStyle), клик по новому слою обрабатывается
 * там же, где и клик по тайловой линии (свойства фичи собраны в том же формате).
 */
const SOURCE_ID = 'martin.scr_tracks_mvt';
const RUNNER_SOURCE = 'scr-runner';
const RUNNER_LAYER = 'scr-runner-point';
const MARKS_SOURCE = 'scr-seg-marks';
const MARKS_LAYER = 'scr-seg-marks-point';
const LINE_SOURCE = 'scr-selected-line';
export const SCR_SELECTED_LINE_LAYER = 'scr-selected-line-layer';
// Суточная шкала для выбора диапазона: continuous 0..24 (24 = конец суток), шаг — час.
// Диапазон требует continuous-домена (см. CLAUDE.md компонента MapTimeline).
const HOURS_RANGE_DOMAIN = {
    type: 'continuous',
    values: [ 0, 24 ],
    step: 1,
    format: h => `${String(Math.round(h)).padStart(2, '0')}:00`,
};
// epoch (local-naive сек) → 'HH:MM'
const fmtEpoch = epoch => {
    const dt = new Date(epoch * 1000);
    return `${String(dt.getUTCHours()).padStart(2, '0')}:${String(dt.getUTCMinutes()).padStart(2, '0')}`;
};
// epoch (local-naive сек) → 'YYYY-MM-DD HH24:MI:SS' — формат ts_from/ts_to в MVT (to_char(...)
// на бэке). Нужен, чтобы фичи клиентской линии (scr-selected-line) были в том же формате
// свойств, что и фичи тайлового слоя — тогда generateScrSegmentMessage работает без правок
// независимо от того, по какому слою кликнули.
const fmtEpochFull = epoch => {
    const dt = new Date(epoch * 1000);
    const p = n => String(n).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())} ${p(dt.getUTCHours())}:${p(dt.getUTCMinutes())}:${p(dt.getUTCSeconds())}`;
};
// 'YYYY-MM-DD' + N дней (та же конвенция, что и TimelineDateControl.shiftDay в MapTimeline.jsx).
const addDaysIso = (iso, delta) => {
    const [ y, m, d ] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + delta);
    return dt.toISOString().slice(0, 10);
};
// epoch → 'DD.MM.YYYY'
const fmtDateLong = epoch => {
    const dt = new Date(epoch * 1000);
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${dt.getUTCFullYear()}`;
};
const SHIFT_TICKS_COUNT = 6; // фиксированное число делений шкалы смены — интервал между ними
// растягивается/сжимается под фактическую длительность (шкала суток берёт готовый пресет).
const DAY_SEC = 86400;
/**
 * Домен таймлайна для режима смены: [начало−1ч, конец+1ч] — контекст до/после реальной смены.
 * Деления под рельсой — только «HH:MM» (SHIFT_TICKS_COUNT равномерных + явная засечка на
 * каждую попавшую в диапазон полночь, см. §3 — полночь размечается отдельной пунктирной
 * отсечкой через dayBoundaries, дублировать дату в самой подписи тика не нужно).
 * Если диапазон пересекает сутки — domain.format (текст рядом с Play, §1) ВСЕГДА отдаёт
 * полную дату+время, а не только после фактического пересечения полуночи курсором:
 * иначе формат курсора «прыгает» с «20:00» на «07.07.2026 01:20» неконсистентно.
 * dayBands — по одной записи на календарный день диапазона (для подписи дат отдельной
 * строкой под тиками, §2); dayBoundaries — эпохи полуночей (для пунктирных отсечек, §3).
 */
const buildShiftDomain = (actualMin, actualMax) => {
    const domainMin = actualMin - 3600;
    const domainMax = actualMax + 3600;
    const span = domainMax - domainMin || 1;
    const evenPositions = Array.from({ length: SHIFT_TICKS_COUNT }, (_, i) => (
        domainMin + (span * i) / (SHIFT_TICKS_COUNT - 1)
    ));
    const midnights = [];
    for (let m = Math.ceil(domainMin / DAY_SEC) * DAY_SEC; m < domainMax; m += DAY_SEC) midnights.push(m);
    const spansTwoDays = midnights.length > 0;
    // Полночь не добавляем тиком, если она слипается с соседним равномерным делением
    // (подписи наложились бы) — сама пунктирная отсечка (dayBoundaries) рисуется всё равно.
    const minGap = span * 0.04;
    const ticks = [ ...evenPositions ];
    midnights.forEach(m => { if (ticks.every(p => Math.abs(p - m) > minGap)) ticks.push(m); });
    ticks.sort((a, b) => a - b);
    const dayStarts = [ domainMin, ...midnights ];
    const dayEnds = [ ...midnights, domainMax ];
    const dayBands = spansTwoDays
        ? dayStarts.map((from, i) => ({ from, to: dayEnds[i], label: fmtDateLong(from) }))
        : [];
    return {
        domain: {
            type: 'continuous',
            values: [ domainMin, domainMax ],
            step: Math.max(1, Math.round(span / 600)),
            ticks: ticks.map(pos => ({ value: pos, label: fmtEpoch(pos) })),
            format: pos => (spansTwoDays ? `${fmtDateLong(pos)} ${fmtEpoch(pos)}` : fmtEpoch(pos)),
        },
        dayBands,
        dayBoundaries: midnights,
    };
};
// Активность сегмента из MVT-строки: [{device_code, action_label, active_ride, cargo,
// battery_swapped, service_scooter}] (этап 12; moves_with_executor — устаревший флаг, фолбэк).
const parseActivity = raw => {
    if (typeof raw !== 'string' || raw.length < 3) return [];
    try {
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
    } catch (e) {
        return [];
    }
};
// endpoint_infra сегмента (jsonb из MVT приходит строкой): { from/to: {parking, warehouse, charging} }.
const parseEndpointInfra = raw => {
    if (raw && typeof raw === 'object') return raw;
    if (typeof raw !== 'string' || raw.length < 2) return null;
    try { const o = JSON.parse(raw); return o && typeof o === 'object' ? o : null } catch (e) { return null }
};
// Сторона endpoint_infra → нормализованный набор id инфраструктуры (null-поля отбрасываем).
const infraSide = side => {
    if (!side || typeof side !== 'object') return null;
    const out = {};
    if (typeof side.parking === 'number') out.parking = side.parking;
    if (typeof side.warehouse === 'number') out.warehouse = side.warehouse;
    if (typeof side.charging === 'number') out.charging = side.charging;
    return Object.keys(out).length ? out : null;
};
const dist2 = (a, b) => { const dx = a[0] - b[0]; const dy = a[1] - b[1]; return (dx * dx) + (dy * dy) };
// Геометрия фичи → массив фрагментов-полилиний (LineString / MultiLineString).
const featureFragments = geom => {
    if (!geom) return [];
    if (geom.type === 'LineString') return geom.coordinates?.length >= 2 ? [ geom.coordinates ] : [];
    if (geom.type === 'MultiLineString') return (geom.coordinates || []).filter(c => c.length >= 2);
    return [];
};
// Сшивка фрагментов одного сегмента (MVT режет линию по границам тайлов, и querySourceFeatures
// отдаёт по куску на тайл) в единую полилинию — жадно по ближайшим концам, иначе бегунок
// едет по обрывку и телепортируется в истинный конец сегмента.
const stitchFragments = frags => {
    if (frags.length <= 1) return frags[0] || [];
    const rem = frags.slice();
    let chain = rem.shift().slice();
    while (rem.length) {
        const head = chain[0];
        const tail = chain[chain.length - 1];
        let best = null;
        rem.forEach((fr, idx) => {
            const s = fr[0];
            const e = fr[fr.length - 1];
            [
                { d: dist2(tail, s), where: 'tail', flip: false },
                { d: dist2(tail, e), where: 'tail', flip: true },
                { d: dist2(head, e), where: 'head', flip: false },
                { d: dist2(head, s), where: 'head', flip: true },
            ].forEach(c => { if (!best || c.d < best.d) best = { ...c, idx }; });
        });
        const fr = rem.splice(best.idx, 1)[0];
        const piece = best.flip ? fr.slice().reverse() : fr;
        if (best.where === 'tail') {
            chain = chain.concat(dist2(tail, piece[0]) === 0 ? piece.slice(1) : piece);
        } else {
            chain = (dist2(head, piece[piece.length - 1]) === 0 ? piece.slice(0, -1) : piece).concat(chain);
        }
    }
    return chain;
};
// Сегменты из набора фич [{ props, fragments }] (общий путь для querySourceFeatures и прямой
// загрузки тайлов): группируем по seg_id, сшиваем фрагменты, ориентируем по смежности.
const buildSegments = items => {
    // Накапливаем ВСЕ фрагменты каждого сегмента (по seg_id), потом сшиваем — не дедупим по первому.
    const bySeg = new Map();
    items.forEach(({ props: p, fragments }) => {
        if (!Number.isFinite(Number(p.ts_from_epoch)) || !Number.isFinite(Number(p.ts_to_epoch))) return; // только z>=12
        if (!fragments || !fragments.length) return;
        const key = p.seg_id ?? `${p.ts_from_epoch}-${p.ts_to_epoch}`;
        if (!bySeg.has(key)) {
            const ei = parseEndpointInfra(p.endpoint_infra);
            bySeg.set(key, {
                tsFrom: Number(p.ts_from_epoch),
                tsTo: Number(p.ts_to_epoch),
                roleTarget: p.role_target,
                activity: parseActivity(p.activity_json),
                // Инфра на концах сегмента (этап 14): { parking, warehouse, charging } или null.
                infraFrom: infraSide(ei && ei.from),
                infraTo: infraSide(ei && ei.to),
                segLenM: Number(p.seg_len_m),
                durS: Number(p.dur_s),
                transitMode: p.transit_mode || null, // 'на месте' | 'пешком/самокат' | 'авто' (этап 12)
                frags: [],
            });
        }
        bySeg.get(key).frags.push(...fragments);
    });
    const segs = Array.from(bySeg.values())
        .map(s => { const { frags, ...rest } = s; return { ...rest, coords: stitchFragments(frags) } })
        .filter(s => s.coords.length >= 2)
        .sort((a, b) => a.tsFrom - b.tsFrom);
    // Ориентация: сегменты смежны по времени и пространству — разворачиваем каждый так,
    // чтобы его начало примыкало к концу предыдущего (сшивка не гарантирует направление).
    if (segs.length >= 2) {
        // Сначала первый сегмент: его конец должен быть ближе к любому из концов второго.
        const c0 = segs[0].coords;
        const n = segs[1].coords;
        const nearTo = pt => Math.min(dist2(pt, n[0]), dist2(pt, n[n.length - 1]));
        if (nearTo(c0[0]) < nearTo(c0[c0.length - 1])) segs[0].coords = c0.slice().reverse();
        // Затем распространяем ориентацию вперёд от уже выверенного первого.
        for (let i = 1; i < segs.length; i++) {
            const prevEnd = segs[i - 1].coords[segs[i - 1].coords.length - 1];
            const c = segs[i].coords;
            if (dist2(prevEnd, c[c.length - 1]) < dist2(prevEnd, c[0])) segs[i].coords = c.slice().reverse();
        }
    }
    return segs;
};
// Сегменты выбранной смены из загруженных тайлов (querySourceFeatures, ограничено вьюпортом).
const collectShiftSegments = (map, shiftId) => {
    if (!map.getSource(SOURCE_ID)) return [];
    let feats = [];
    try {
        feats = map.querySourceFeatures(SOURCE_ID, {
            sourceLayer: SOURCE_ID,
            filter: [ '==', [ 'get', 'shift_id' ], shiftId ],
        });
    } catch (e) {
        return [];
    }
    const segs = buildSegments(feats.map(f => ({ props: f.properties || {}, fragments: featureFragments(f.geometry) })));
    return segs;
};
// Bbox смены (xmin,ymin,xmax,ymax в 4326). На z>=12 берём из свойства bbox; на z<12 (overview,
// свойства bbox нет) считаем экстент по геометрии загруженной агрегированной линии смены —
// этого достаточно, чтобы вычислить диапазон z12-тайлов для прямой загрузки.
const shiftBbox = (map, shiftId) => {
    try {
        const fs = map.querySourceFeatures(SOURCE_ID, {
            sourceLayer: SOURCE_ID,
            filter: [ '==', [ 'get', 'shift_id' ], shiftId ],
        });
        for (const f of fs) {
            const b = f.properties?.bbox;
            if (typeof b === 'string') {
                const [ xmin, ymin, xmax, ymax ] = b.split(',').map(Number);
                if ([ xmin, ymin, xmax, ymax ].every(Number.isFinite)) return [ [ xmin, ymin ], [ xmax, ymax ] ];
            }
        }
        // Фолбэк по геометрии (overview/z<12 без свойства bbox).
        let xmin = Infinity; let ymin = Infinity; let xmax = -Infinity; let ymax = -Infinity; let any = false;
        const scan = c => {
            if (Array.isArray(c[0])) { c.forEach(scan); return }
            any = true;
            if (c[0] < xmin) xmin = c[0];
            if (c[0] > xmax) xmax = c[0];
            if (c[1] < ymin) ymin = c[1];
            if (c[1] > ymax) ymax = c[1];
        };
        fs.forEach(f => { if (f.geometry?.coordinates) scan(f.geometry.coordinates); });
        if (any) return [ [ xmin, ymin ], [ xmax, ymax ] ];
    } catch (e) { /* нет загруженных фич смены */ }
    return null;
};
// Подпись манипуляции с самокатом у точки. side: 'in' — прибыл на сегменте до точки,
// 'out' — убыл на сегменте после точки. Спецдействия (СЦ, замена АКБ) сохраняют свой ярлык.
// Перевозку определяет флаг cargo (этап 12), фолбэк — устаревший moves_with_executor.
const pointActionLabel = (entry, side) => {
    const lbl = entry.action_label || '';
    if (entry.battery_swapped || /батаре/i.test(lbl)) return lbl || 'замена батареи';
    if (/СЦ|обслуж/i.test(lbl)) return lbl;
    const cargo = entry.cargo ?? entry.moves_with_executor;
    if (cargo) return side === 'in' ? 'привоз на эту стоянку' : 'увоз с этой стоянки';
    return lbl || '—';
};
// Список манипуляций у точки: активность сегмента до (in) и после (out) точки, дедуп по коду+подписи.
const pointScooterLines = (prevSeg, nextSeg) => {
    const seen = new Set();
    const lines = [];
    const add = (seg, side) => {
        if (!seg) return;
        seg.activity.forEach(e => {
            const code = e.device_code || '?';
            const label = pointActionLabel(e, side);
            const k = `${code}|${label}`;
            if (seen.has(k)) return;
            seen.add(k);
            // Ключи совпадают с полями API — их читает generatePopupMessages.js по этим же именам.
            // eslint-disable-next-line camelcase
            lines.push({ code, label, event_conf: e.event_conf, conf_reason: e.conf_reason });
        });
    };
    add(prevSeg, 'in');
    add(nextSeg, 'out');
    return lines;
};
// Точка на ломаной на доле f (0..1) суммарной длины.
const pointAlong = (coords, f) => {
    if (f <= 0) return coords[0];
    if (f >= 1) return coords[coords.length - 1];
    const dist = [];
    let total = 0;
    for (let i = 1; i < coords.length; i++) {
        const dx = coords[i][0] - coords[i - 1][0];
        const dy = coords[i][1] - coords[i - 1][1];
        const d = Math.hypot(dx, dy);
        dist.push(d);
        total += d;
    }
    let target = f * total;
    for (let i = 0; i < dist.length; i++) {
        if (target <= dist[i] || i === dist.length - 1) {
            const r = dist[i] ? target / dist[i] : 0;
            return [
                coords[i][0] + (coords[i + 1][0] - coords[i][0]) * r,
                coords[i][1] + (coords[i + 1][1] - coords[i][1]) * r,
            ];
        }
        target -= dist[i];
    }
    return coords[coords.length - 1];
};
// Скорость исполнителя на сегменте, содержащем момент epoch, км/ч (по seg_len_m / dur_s),
// вместе с transit_mode сегмента — нужен, чтобы решить, «нормальная» ли эта скорость.
const speedKmhAt = (segments, epoch) => {
    const seg = segments.find(s => epoch >= s.tsFrom && epoch <= s.tsTo);
    if (!seg) return null;
    if (seg.transitMode === 'на месте') return { speed: 0, transitMode: seg.transitMode }; // вырожденный сегмент-точка (этап 12)
    if (!Number.isFinite(seg.segLenM) || !Number.isFinite(seg.durS) || seg.durS <= 0) return null;
    return { speed: (seg.segLenM / seg.durS) * 3.6, transitMode: seg.transitMode };
};
// «Нормальный» диапазон скорости по режиму перемещения — вне него подпись подсвечивается
// жёлтым (сигнал возможной ошибки скорости/трека), внутри — обычным белым.
const SPEED_NORMAL_RANGE = {
    'пешком/самокат': [ 3, 20 ],
    'авто': [ 20, 80 ],
};
const isSpeedNormal = (speed, transitMode) => {
    const range = SPEED_NORMAL_RANGE[transitMode];
    if (!range) return true; // 'на месте'/'нет гео'/неизвестный режим — диапазон не задан, не подсвечиваем
    return speed >= range[0] && speed <= range[1];
};
// Позиция исполнителя на момент epoch по списку сегментов.
const runnerPosition = (segments, epoch) => {
    if (!segments.length) return null;
    if (epoch <= segments[0].tsFrom) return segments[0].coords[0];
    const last = segments[segments.length - 1];
    if (epoch >= last.tsTo) return last.coords[last.coords.length - 1];
    for (const s of segments) {
        if (epoch >= s.tsFrom && epoch <= s.tsTo) {
            const f = s.tsTo > s.tsFrom ? (epoch - s.tsFrom) / (s.tsTo - s.tsFrom) : 0;
            return pointAlong(s.coords, f);
        }
    }
    // в разрыве между сегментами — держим точку на конце предыдущего
    let prev = segments[0];
    for (const s of segments) { if (s.tsFrom > epoch) break; prev = s }
    return prev.coords[prev.coords.length - 1];
};
const ScrTracksTimelineComponent = ({
    scrTracksHourFrom, scrTracksHourTo, setScrTracksHourRange,
    selectedScrTracksDate, setScrTracksDate,
    scrTracksSelectedShiftId,
}) => {
    // eslint-disable-next-line no-underscore-dangle
    const map = typeof window !== 'undefined' ? window.__map : null;
    const detailsMode = scrTracksSelectedShiftId !== null && typeof scrTracksSelectedShiftId !== 'undefined';
    // Дата в режиме суток выбирается свободно (без ограничения диапазоном): пикер не связан
    // метаданными тайла — доступна любая дата, дефолт задаётся в reducers/map.js (текущий сэмпл).
    // ── режим смены: сегменты + домен + риски-отсечки + точка-бегунок ─────────
    const [ segments, setSegments ] = React.useState([]);
    const [ cursorEpoch, setCursorEpoch ] = React.useState(null);
    const ensureRunnerLayer = React.useCallback(() => {
        if (!map) return;
        // Линия выбранной смены — добавляем первой, чтобы отсечки/бегунок рисовались поверх неё.
        // Цвет выставится в setLine при первой загрузке сегментов (роль там пока не известна).
        if (!map.getSource(LINE_SOURCE)) {
            map.addSource(LINE_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        }
        if (!map.getLayer(SCR_SELECTED_LINE_LAYER)) {
            map.addLayer({
                id: SCR_SELECTED_LINE_LAYER,
                type: 'line',
                source: LINE_SOURCE,
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: {
                    'line-color': '#757575',
                    'line-width': SCR_TRACKS_WIDTH_BASE,
                    'line-opacity': 0.9,
                },
            });
        }
        // Точки временных отсечек между сегментами — те же риски, что и на таймлайне.
        // Слой добавляем следующим, чтобы бегунок рисовался поверх отсечек, а обе точечные
        // подложки — поверх линии.
        if (!map.getSource(MARKS_SOURCE)) {
            map.addSource(MARKS_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        }
        if (!map.getLayer(MARKS_LAYER)) {
            map.addLayer({
                id: MARKS_LAYER,
                type: 'circle',
                source: MARKS_SOURCE,
                paint: {
                    'circle-radius': 4,
                    'circle-color': '#f6515c',
                    'circle-stroke-color': '#2b2b2b', // цвет тёмного фона карты
                    'circle-stroke-width': 3,
                    'circle-stroke-opacity': 0.5,
                },
            });
        }
        if (!map.getSource(RUNNER_SOURCE)) {
            map.addSource(RUNNER_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        }
        if (!map.getLayer(RUNNER_LAYER)) {
            map.addLayer({
                id: RUNNER_LAYER,
                type: 'circle',
                source: RUNNER_SOURCE,
                paint: {
                    'circle-radius': 7,
                    'circle-color': '#ffffff',
                    'circle-stroke-color': '#f6515c',
                    'circle-stroke-width': 3,
                },
            });
        }
    }, [ map ]);
    const removeRunnerLayer = React.useCallback(() => {
        if (!map) return;
        if (map.getLayer(RUNNER_LAYER)) map.removeLayer(RUNNER_LAYER);
        if (map.getSource(RUNNER_SOURCE)) map.removeSource(RUNNER_SOURCE);
        if (map.getLayer(MARKS_LAYER)) map.removeLayer(MARKS_LAYER);
        if (map.getSource(MARKS_SOURCE)) map.removeSource(MARKS_SOURCE);
        if (map.getLayer(SCR_SELECTED_LINE_LAYER)) map.removeLayer(SCR_SELECTED_LINE_LAYER);
        if (map.getSource(LINE_SOURCE)) map.removeSource(LINE_SOURCE);
        clearScrKeyPoints();
    }, [ map ]);
    // Линия выбранной смены — из ТЕХ ЖЕ segments, что и бегунок/риски (см. комментарий у
    // констант выше): гарантированно совпадает с траекторией воспроизведения. Каждый сегмент —
    // отдельная Feature со свойствами в формате MVT-тайла (ts_from/ts_to как строки, activity_json
    // как JSON-строка) — чтобы клик по этому слою обрабатывался generateScrSegmentMessage
    // без правок, так же, как клик по исходному тайловому слою.
    const setLine = React.useCallback((segs, shiftId) => {
        if (!map?.getSource(LINE_SOURCE)) return;
        if (!segs.length) {
            map.getSource(LINE_SOURCE).setData({ type: 'FeatureCollection', features: [] });
            return;
        }
        map.setPaintProperty(SCR_SELECTED_LINE_LAYER, 'line-color', roleColor(segs[0].roleTarget));
        map.getSource(LINE_SOURCE).setData({
            type: 'FeatureCollection',
            features: segs.map(s => ({
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: s.coords },
                properties: {
                    'shift_id': shiftId,
                    'ts_from': fmtEpochFull(s.tsFrom),
                    'ts_to': fmtEpochFull(s.tsTo),
                    'transit_mode': s.transitMode,
                    'seg_len_m': s.segLenM,
                    'dur_s': s.durS,
                    'activity_json': JSON.stringify(s.activity),
                },
            })),
        });
    }, [ map ]);
    // Точки-отсечки: старт каждого сегмента + финальный конец (совпадают с рисками таймлайна).
    // Если у точки есть инфра-якорь (endpoint_infra: парковка/СЦ/склад/зарядка) — её НЕ рисуем
    // отдельным маркером: её представляет точка слоя scr_infra, а payload (время, манипуляции)
    // публикуем в реестр по ключам инфра-якорей — попап читает его в обработчике клика scr_infra.
    // Точки без инфры рисуем обычным маркером-кружком (попап — generateScrPointMessage).
    const setMarks = React.useCallback(segs => {
        if (!map?.getSource(MARKS_SOURCE)) return;
        if (!segs.length) {
            map.getSource(MARKS_SOURCE).setData({ type: 'FeatureCollection', features: [] });
            clearScrKeyPoints();
            return;
        }
        // Описания точек: i=0 старт смены, i=n конец; внутренние — границы между сегментами.
        // infra — набор id инфры на этой границе (с конца предыдущего / начала следующего сегмента).
        const points = [];
        segs.forEach((s, i) => {
            const prevSeg = i > 0 ? segs[i - 1] : null;
            points.push({
                coord: s.coords[0],
                epoch: prevSeg ? prevSeg.tsTo : s.tsFrom,
                infra: (prevSeg && prevSeg.infraTo) || s.infraFrom,
                prevSeg,
                nextSeg: s,
            });
        });
        const last = segs[segs.length - 1];
        points.push({
            coord: last.coords[last.coords.length - 1],
            epoch: last.tsTo,
            infra: last.infraTo,
            prevSeg: last,
            nextSeg: null,
        });
        // Реестр: ключевую точку, совпавшую с инфрой, доступной по ключам её якорей.
        const registry = {};
        points.forEach(pt => {
            if (!pt.infra) return;
            const payload = {
                time: fmtEpoch(pt.epoch),
                scooters: pointScooterLines(pt.prevSeg, pt.nextSeg),
            };
            if (typeof pt.infra.parking === 'number') registry[`parking:${pt.infra.parking}`] = payload;
            if (typeof pt.infra.warehouse === 'number') registry[`warehouse:${pt.infra.warehouse}`] = payload;
            if (typeof pt.infra.charging === 'number') registry[`charging:${pt.infra.charging}`] = payload;
        });
        setScrKeyPoints(registry);
        const fillColor = roleColor(segs[0].roleTarget);
        map.setPaintProperty(MARKS_LAYER, 'circle-color', fillColor);
        map.getSource(MARKS_SOURCE).setData({
            type: 'FeatureCollection',
            // Рисуем только точки БЕЗ инфры — совпавшие с инфрой представлены точками scr_infra.
            features: points.filter(pt => !pt.infra).map(pt => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: pt.coord },
                properties: {
                    scrPoint: JSON.stringify({
                        time: fmtEpoch(pt.epoch),
                        parking: null,
                        scooters: pointScooterLines(pt.prevSeg, pt.nextSeg),
                    }),
                },
            })),
        });
    }, [ map ]);
    const setRunner = React.useCallback((segs, epoch) => {
        if (!map?.getSource(RUNNER_SOURCE)) return;
        const pos = runnerPosition(segs, epoch);
        map.getSource(RUNNER_SOURCE).setData({
            type: 'FeatureCollection',
            features: pos ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: pos }, properties: {} }] : [],
        });
    }, [ map ]);

    // вход/выход режима смены: собрать сегменты, поставить точку на старт; на выходе — убрать слой
    React.useEffect(() => {
        if (!map || !detailsMode) { removeRunnerLayer(); setSegments([]); setCursorEpoch(null); return () => {} }
        let cancelled = false;
        ensureRunnerLayer();
        const apply = segs => {
            if (cancelled) return;
            setSegments(segs);
            const start = segs.length ? segs[0].tsFrom : null;
            setCursorEpoch(start);
            setMarks(segs);
            setLine(segs, scrTracksSelectedShiftId);
            if (start !== null) setRunner(segs, start);
        };
        let gotFull = false;
        // Сразу показываем то, что есть во вьюпорте.
        apply(collectShiftSegments(map, scrTracksSelectedShiftId));
        // Запасной путь: пересбор по вьюпорту после idle (если прямая загрузка тайлов не сработала).
        const recollect = () => { if (!cancelled && !gotFull) apply(collectShiftSegments(map, scrTracksSelectedShiftId)); };
        // Полный набор сегментов — прямая загрузка z12-тайлов по bbox смены, НЕЗАВИСИМО от
        // зума/положения карты (камеру не двигаем). bbox берём из любой загруженной фичи смены.
        const bounds = shiftBbox(map, scrTracksSelectedShiftId);
        if (bounds) {
            // DA-15115 — смена может переваливать через полночь: догружаем тайлы и за
            // selectedScrTracksDate, и за следующие сутки, иначе часть смены после полуночи
            // (сегменты с ts_from на следующую дату) не попадёт в выборку и трек обрежется.
            const nextDate = addDaysIso(selectedScrTracksDate, 1);
            Promise.all([
                fetchShiftTileFeatures(bounds, scrTracksSelectedShiftId, selectedScrTracksDate),
                fetchShiftTileFeatures(bounds, scrTracksSelectedShiftId, nextDate),
            ])
                .then(([ a, b ]) => {
                    const items = [ ...a, ...b ];
                    if (!cancelled && items.length) { gotFull = true; apply(buildSegments(items)) }
                })
                .catch(() => { /* фолбэк — пересбор по вьюпорту на idle */ });
        }
        map.once('idle', recollect);
        return () => { cancelled = true; map.off('idle', recollect); removeRunnerLayer() };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ map, detailsMode, scrTracksSelectedShiftId ]);

    if (!map) return null;

    if (detailsMode) {
        // Сегменты ещё грузятся (или их нет) — показываем пустой таймлайн.
        // Возврат к слою всех треков теперь в легенде (ScrTracksFilter), не на таймлайне.
        if (!segments.length) {
            return (
                <MapTimeline
                    domain={{ type: 'continuous', values: [ 0, 1 ], step: 1, format: () => '' }}
                    cursor={0}
                />
            );
        }
        const min = segments[0].tsFrom;
        const max = segments[segments.length - 1].tsTo;
        // DA-15115 — шкала шире фактической смены на 1ч с каждой стороны (видно контекст
        // до/после); dayBands/dayBoundaries — подписи дат и пунктирные отсечки полуночи (§2, §3).
        const { domain, dayBands, dayBoundaries } = buildShiftDomain(min, max);
        // риски-отсечки: начало каждого сегмента + финальный конец
        const eventTicks = [
            ...segments.map(s => ({ t: s.tsFrom, colors: [ '#f6515c' ] })),
            { t: max, colors: [ '#f6515c' ] },
        ];
        const onCursor = epoch => { setCursorEpoch(epoch); setRunner(segments, epoch) };
        const span = max - min || 1;
        const speedInfo = speedKmhAt(segments, cursorEpoch ?? min);
        const playbackInfo = speedInfo ? (
            <span style={{ color: isSpeedNormal(speedInfo.speed, speedInfo.transitMode) ? '#fff' : '#ffd24d' }}>
                {speedInfo.speed.toFixed(1)} км/ч
            </span>
        ) : null;
        return (
            <MapTimeline
                domain={domain}
                cursor={cursorEpoch ?? min}
                onCursorChange={onCursor}
                onTick={onCursor}
                playbackInfo={playbackInfo}
                playback={{ enabled: true, speed: span / 30 }}
                eventTicks={eventTicks}
                bottomBands={dayBands}
                dayBoundaries={dayBoundaries}
                highlightRange={[ min, max ]}
            />
        );
    }

    // Режим всех треков — выбор диапазона часов (два бегунка). По умолчанию [0,24] — целые
    // сутки, ничего не приглушается; при сужении треки вне диапазона бледнеют (Map.jsx).
    const from = typeof scrTracksHourFrom === 'number' ? scrTracksHourFrom : 0;
    const to = typeof scrTracksHourTo === 'number' ? scrTracksHourTo : 24;
    const onRange = ([ a, b ]) => {
        const nextFrom = Math.round(a);
        const nextTo = Math.round(b);
        if (nextFrom !== from || nextTo !== to) {
            setScrTracksHourRange({ scrTracksHourFrom: nextFrom, scrTracksHourTo: nextTo });
        }
    };
    return (
        <MapTimeline
            domain={HOURS_RANGE_DOMAIN}
            range={[ from, to ]}
            onRangeChange={onRange}
            minorTicks
            dateControl={{
                value: selectedScrTracksDate,
                onChange: setScrTracksDate,
                // без minDate/maxDate — любая дата доступна к выбору
            }}
        />
    );
};
const mapStateToProps = ({
    map: { selectedScrTracksDate, scrTracksSelectedShiftId },
    mapFilters: { scrTracksHourFrom, scrTracksHourTo },
}) => ({ selectedScrTracksDate, scrTracksSelectedShiftId, scrTracksHourFrom, scrTracksHourTo });
const mapDispatchToProps = dispatch => ({
    setScrTracksHourRange: payload => dispatch(onChangeMapFiltersMulti(payload)),
    setScrTracksDate: date => dispatch({ type: T.SET_SCR_TRACKS_DATE, payload: { selectedScrTracksDate: date } }),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(ScrTracksTimelineComponent);

export const ScrTracksTimeline = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Треки исполнителей`
);
